import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Shield, CheckCircle2, MapPin, Star, Upload, Gavel, Truck, Sparkles } from "lucide-react";
import { BRAND, CATEGORIES, HERO_BANNERS } from "../constants/brand";
import { MOCK_VENDORS, MOCK_TESTIMONIALS } from "../data/mock";

function HeroCarousel() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % HERO_BANNERS.length), 6000);
    return () => clearInterval(t);
  }, []);
  const b = HERO_BANNERS[i];
  return (
    <section className="relative isolate overflow-hidden bg-neutral-950 text-white grain" data-testid="home-hero">
      <div className="absolute inset-0 -z-10">
        <AnimatePresence mode="wait">
          <motion.img
            key={b.id}
            src={b.bg}
            alt=""
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 0.35, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-black/85" />
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-24 md:grid-cols-12 md:px-6 md:py-32">
        <div className="md:col-span-8">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-lime-400/40 bg-lime-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-lime-300">
            <span className="h-1.5 w-1.5 rounded-full bg-lime-400 pulse-dot" />
            {b.eyebrow}
          </div>
          <AnimatePresence mode="wait">
            <motion.h1
              key={b.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.6 }}
              className="font-display text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
              data-testid="hero-title"
            >
              {b.title}
              <br />
              <span className="text-lime-400">{b.titleAccent}</span>
            </motion.h1>
          </AnimatePresence>
          <p className="mt-6 max-w-xl text-base text-neutral-300 md:text-lg" data-testid="hero-body">
            {b.body}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={b.cta.to}
              className="btn-offset inline-flex items-center gap-2 rounded-md bg-lime-400 px-5 py-3 text-sm font-bold text-neutral-950 hover:bg-lime-300"
              data-testid="hero-cta-primary"
            >
              {b.cta.label} <ArrowRight size={16} />
            </Link>
            <a
              href={b.secondaryCta.to}
              className="rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white hover:border-lime-400 hover:text-lime-400"
              data-testid="hero-cta-secondary"
            >
              {b.secondaryCta.label}
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-6 text-xs text-neutral-400">
            <div className="flex items-center gap-2"><Shield size={16} className="text-lime-400" /> Escrow-protected payments</div>
            <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-lime-400" /> Verified vendors only</div>
            <div className="flex items-center gap-2"><MapPin size={16} className="text-lime-400" /> Serving Delhi NCR</div>
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-5 backdrop-blur" data-testid="hero-side-card">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-neutral-400">Live now</span>
              <span className="rounded-full bg-lime-400/15 px-2 py-0.5 text-xs font-bold text-lime-300">3 bids</span>
            </div>
            <p className="mt-3 font-display text-lg font-bold">Business cards · 500 qty · Matt</p>
            <p className="mt-1 text-xs text-neutral-400">Countdown: 01:24:12 · Delhi NCR</p>
            <div className="mt-4 space-y-2">
              {[{ v: "Bhairo Prints", p: "₹2,200", tag: "Lowest" }, { v: "Shakti Digital", p: "₹2,450", tag: "Best rated" }, { v: "QuickPress", p: "₹2,350", tag: "Fastest" }].map((r) => (
                <div key={r.v} className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm">
                  <div>
                    <div className="font-semibold text-white">{r.v}</div>
                    <div className="text-xs text-neutral-400">{r.tag}</div>
                  </div>
                  <div className="font-mono text-lime-400">{r.p}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Slide indicators */}
          <div className="mt-6 flex items-center gap-2" data-testid="hero-indicators">
            {HERO_BANNERS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                aria-label={`Slide ${idx + 1}`}
                data-testid={`hero-indicator-${idx}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-lime-400" : "w-4 bg-white/30"}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="border-y border-white/10 bg-black/80 py-3">
        <div className="flex overflow-hidden">
          <div className="marquee-track flex shrink-0 items-center gap-10 whitespace-nowrap px-6 font-display text-2xl font-bold text-neutral-500">
            {Array.from({ length: 2 }).map((_, i) => (
              <React.Fragment key={i}>
                <span>PRINT ANYTHING.</span>
                <span className="text-lime-400">FROM ANYWHERE.</span>
                <span>LIVE BIDDING.</span>
                <span className="text-lime-400">ESCROW-SAFE.</span>
                <span>VERIFIED VENDORS.</span>
                <span className="text-lime-400">DELHI NCR.</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Upload, title: "Upload your file", desc: "PDF, AI, CDR, PSD, PNG or JPG. Our AI checks resolution, bleed and colour mode instantly." },
    { icon: Gavel, title: "Live bidding", desc: "Verified vendors near you compete in real time. Compare price, rating and delivery — you decide." },
    { icon: Truck, title: "Track until delivered", desc: "Watch printing → QC → out for delivery, live on one screen. Like a food-delivery app for print." },
  ];
  return (
    <section id="how" className="border-b border-neutral-200" data-testid="home-how-it-works">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Simple process</span>
            <h2 className="mt-2 font-display text-4xl font-bold tracking-tight md:text-5xl">How EarlyPrint works.</h2>
          </div>
          <p className="max-w-md text-sm text-neutral-600">Three steps from file to your door — usually within a day.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s, idx) => (
            <div key={s.title} className="group relative overflow-hidden rounded-lg border border-neutral-200 bg-white p-8 transition-transform hover:-translate-y-1" data-testid={`how-step-${idx + 1}`}>
              <div className="absolute right-6 top-6 font-display text-6xl font-black text-neutral-100">0{idx + 1}</div>
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-lime-400 text-neutral-950">
                <s.icon size={22} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold">{s.title}</h3>
              <p className="mt-3 text-sm text-neutral-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustPillars() {
  const pillars = [
    { title: "Escrow-protected payments", body: "Your payment is held securely and only released to the vendor after you confirm delivery. Your money is never at risk.", icon: Shield },
    { title: "Verified vendors only", body: "Every print shop on EarlyPrint is personally vetted before they can accept orders. No random strangers.", icon: CheckCircle2 },
    { title: "Live order tracking", body: "See every status update — from printing to dispatch to delivery — in real time.", icon: MapPin },
  ];
  return (
    <section className="border-b border-neutral-200 bg-neutral-50" data-testid="home-trust">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Built around trust</span>
            <h2 className="mt-2 font-display text-4xl font-bold tracking-tight md:text-5xl">Why choose EarlyPrint.</h2>
            <p className="mt-4 text-sm text-neutral-600">
              We designed EarlyPrint for customers who've been burned by unclear pricing and missing orders. Three promises we don't compromise on.
            </p>
          </div>
          <div className="grid gap-4 md:col-span-8 md:grid-cols-3">
            {pillars.map((p) => (
              <div key={p.title} className="rounded-lg border border-neutral-200 bg-white p-6" data-testid={`trust-${p.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-neutral-950 text-lime-400">
                  <p.icon size={18} />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold">{p.title}</h3>
                <p className="mt-2 text-sm text-neutral-600">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Categories() {
  return (
    <section id="what-we-print" className="border-b border-neutral-200" data-testid="home-categories">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">What we print</span>
            <h2 className="mt-2 font-display text-4xl font-bold tracking-tight md:text-5xl">Browse by category.</h2>
          </div>
          <p className="max-w-md text-sm text-neutral-600">Select a category to start your order — we'll show live pricing and turnaround times from verified vendors near you.</p>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {CATEGORIES.map((c) => (
            <Link
              to="/signup"
              key={c.key}
              data-testid={`cat-${c.key}`}
              className="group relative overflow-hidden rounded-lg border border-neutral-200 bg-white transition-transform hover:-translate-y-1"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={c.img} alt={c.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-lime-300">From {c.price}</div>
                    <div className="mt-1 font-display text-lg font-bold text-white">{c.label}</div>
                  </div>
                  <div className="rounded-full bg-lime-400 p-2 text-neutral-950 opacity-0 transition-opacity group-hover:opacity-100">
                    <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Link
            to="/signup"
            className="btn-offset inline-flex items-center gap-2 rounded-md bg-neutral-950 px-6 py-3 text-sm font-bold text-white hover:bg-neutral-800"
            data-testid="cat-cta"
          >
            Place Order & Get Quotation With Live Bidding <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function VendorsShowcase() {
  return (
    <section className="border-b border-neutral-200 bg-neutral-950 text-white grain" data-testid="home-vendors">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-400">Verified network</span>
            <h2 className="mt-2 font-display text-4xl font-bold tracking-tight md:text-5xl">Top vendors near you.</h2>
          </div>
          <p className="max-w-md text-sm text-neutral-400">Every shop is personally vetted. Zero anonymous sellers.</p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {MOCK_VENDORS.map((v) => (
            <div key={v.name} className="overflow-hidden rounded-lg border border-white/10 bg-white/5" data-testid={`vendor-${v.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={v.img} alt={v.name} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold">{v.name}</h3>
                  <div className="flex items-center gap-1 text-xs text-lime-400"><Star size={12} fill="currentColor" /> {v.rating}</div>
                </div>
                <p className="mt-1 text-xs text-neutral-400">{v.area} · {v.reviews} reviews</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {v.tags.map((t) => (
                    <span key={t} className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-neutral-300">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ForPrintShops() {
  const stats = [
    { k: "₹0", v: "Upfront cost" },
    { k: "24h", v: "Verification" },
    { k: "100%", v: "Escrow-paid" },
    { k: "Delhi NCR", v: "Service area" },
  ];
  return (
    <section className="border-b border-neutral-200" data-testid="home-for-shops">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">For print shops</span>
            <h2 className="mt-2 font-display text-4xl font-bold tracking-tight md:text-5xl">Grow your print shop with EarlyPrint.</h2>
            <p className="mt-5 text-neutral-600">Stop waiting for walk-ins. Get a steady stream of verified, pre-paid orders delivered directly to your WhatsApp — with no upfront investment.</p>

            <ul className="mt-6 space-y-3 text-sm text-neutral-700">
              {[
                "More customers — reach digital-first buyers who don't know where to find a good print shop.",
                "No lead fees like other platforms — you get a direct order, never a paid lead.",
                "No upfront cost — register free, pay only a small commission on completed orders.",
                "Get paid on time — every order is prepaid via escrow, so you always get your money.",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-lime-500" /> {line}</li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/vendor/register" className="btn-offset inline-flex items-center gap-2 rounded-md bg-lime-400 px-5 py-3 text-sm font-bold text-neutral-950 hover:bg-lime-300" data-testid="shops-cta-register">
                Register your shop <ArrowRight size={16} />
              </Link>
              <Link to="/about" className="rounded-md border border-neutral-300 px-5 py-3 text-sm font-semibold hover:border-neutral-900" data-testid="shops-cta-learn">Learn more</Link>
            </div>
          </div>

          <div className="md:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.v} className="rounded-lg border border-neutral-200 bg-white p-6" data-testid={`stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="font-display text-4xl font-black">{s.k}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest text-neutral-500">{s.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-950 p-6 text-sm text-neutral-300">
              <div className="flex items-center gap-2 text-lime-400"><Sparkles size={16} /> Vendor tip</div>
              <p className="mt-2">All order notifications & customer chats happen on WhatsApp — no new app to learn.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="border-b border-neutral-200 bg-neutral-50" data-testid="home-testimonials">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">People love EarlyPrint</span>
        <h2 className="mt-2 font-display text-4xl font-bold tracking-tight md:text-5xl">Words from the field.</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {MOCK_TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-lg border border-neutral-200 bg-white p-6" data-testid={`testimonial-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full" />
                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-neutral-500">{t.role}</div>
                </div>
              </div>
              <p className="mt-4 text-sm text-neutral-700">"{t.quote}"</p>
              <div className="mt-4 flex text-lime-500">
                {[...Array(5)].map((_, i) => <Star size={14} key={i} fill="currentColor" strokeWidth={0} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-lime-400" data-testid="home-final-cta">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 py-16 md:flex-row md:px-6">
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-neutral-950 md:text-4xl">Ready to print?</h2>
          <p className="mt-2 max-w-lg text-sm text-neutral-800">Get live quotes in minutes. Escrow-safe. Delivered to your door in Delhi NCR.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/signup" className="rounded-md bg-neutral-950 px-6 py-3 text-sm font-bold text-white hover:bg-neutral-800" data-testid="final-cta-signup">Get a Quote</Link>
          <a href={BRAND.social.whatsapp} target="_blank" rel="noopener noreferrer" className="rounded-md border-2 border-neutral-950 px-6 py-3 text-sm font-bold text-neutral-950 hover:bg-white" data-testid="final-cta-whatsapp">Chat on WhatsApp</a>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <HeroCarousel />
      <HowItWorks />
      <TrustPillars />
      <Categories />
      <VendorsShowcase />
      <ForPrintShops />
      <Testimonials />
      <FinalCTA />
    </>
  );
}
